import React, {useState, useRef, useEffect} from 'react';
import {Modal, Platform, ScrollView, View} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';

import Button from '../../elements/Button';
import CalendarView from '../../elements/CalendarView';
import Text from '../../elements/Text';
import Divider from '../../elements/Divider';
import Checkbox from '../../elements/Checkbox';
import Popup from '../../elements/Popup';
import LoadingModal from '../LoadingModal';
import ErrorModal from '../ErrorModal';
import OfflineNotice from '../OfflineNotice';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../../theme';
import {BUTTON_TYPES} from '../../../utils/Constants';
import {getUserProfileData} from '../../../utils/GeneralStorage';
import {fetchApi, NERWORK_ERROR} from '../../../utils/ApiHelper';
import {
  EXPORT_BLOOD_SUGAR_LEVEL,
  HTTP_METHODS,
} from '../../../utils/URLConstants';
import {DLog} from '../../../utils/HelperMethods';
import {tID} from '../../../utils/TestIdHelper';

export const SuccessModal = ({
  showSuccseeModal,
  userEmail,
  onPressResend,
  onCloseSuccessModal,
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <Popup
      testID={tID('ExportDataDateRangeModal-Modal-Success')}
      title={'Successfully Shared'}
      visible={showSuccseeModal}
      description={
        <>
          {'Your blood sugar data has been sent to '}
          <Text
            testID={tID('ExportDataDateRangeModal-Text-Email')}
            variation="Body1SemiBold"
            color={colors.textMuted}>
            {userEmail?.current}
          </Text>
          {' to share with others!'}
        </>
      }>
      <Button
        testID={tID('ExportDataDateRangeModal-Button-Resend')}
        style={[styles.button]}
        onPress={onPressResend}
        title={'Resend Again'}
      />
      <Button
        testID={tID('ExportDataDateRangeModal-Button-Close')}
        style={[styles.button, styles.ml16]}
        onPress={onCloseSuccessModal}
        title={'Close'}
        buttonType={BUTTON_TYPES.Filled}
        titleColor={colors.textOnAccent}
      />
    </Popup>
  );
};

const ExportDataDateRangeModal = ({visible, onClose}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const [allSelected, setAllSelected] = useState(false);
  const [showSuccseeModal, setShowSuccseeModal] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [message, setMessage] = useState('');

  const selectedDateRange = useRef(null);
  const userEmail = useRef(null);

  useEffect(() => {
    const getProfile = async () => {
      const profile = await getUserProfileData();
      userEmail.current = profile?.email;
    };
    getProfile();
  }, []);

  const onPressConfirm = () => {
    exportBloodSugarData();
  };

  const onPressResend = () => {
    setShowSuccseeModal(false);
    setTimeout(() => {
      exportBloodSugarData();
    }, 500);
  };

  const onSelectDateRange = (fromDate, toDate) => {
    selectedDateRange.current = {fromDate, toDate};
  };

  const onPressClose = () => {
    setShowErrorModal(false);
  };

  const exportBloodSugarData = () => {
    setShowLoader(true);
    let params = selectedDateRange.current;
    if (allSelected) {
      params = {};
    }
    fetchApi(HTTP_METHODS.POST, EXPORT_BLOOD_SUGAR_LEVEL, params)
      .then(response => {
        DLog(response);
        setShowLoader(false);
        if (response.status == 200) {
          setShowSuccseeModal(true);
        }
      })
      .catch(e => {
        DLog(e);
        setMessage(e);
        setTimeout(() => {
          setShowLoader(false);
          setShowErrorModal(true);
        }, 500);
      });
  };

  const onCloseSuccessModal = () => {
    onClose(false);
    setShowSuccseeModal(false);
  };

  // iOS cannot present a Modal over a Modal — a nested/simultaneous second
  // Modal freezes the app. The success/error popups are therefore rendered as
  // SIBLINGS of this Modal, and this Modal is hidden whenever one of them is
  // up, so only one Modal is ever visible at a time. (This froze the app after
  // a successful glucose-data export.)
  return (
    <>
    <Modal
      testID={tID('ExportDataDateRangeModal-Modal-Main')}
      transparent
      visible={visible && !showSuccseeModal && !showErrorModal}
      onRequestClose={() => onClose(false)}
      animationType="slide">
      <View
        testID={tID('ExportDataDateRangeModal-View-Container')}
        style={Platform.OS === 'web' ? styles.webContainer : styles.container}>
        <SafeAreaView
          style={
            Platform.OS === 'web'
              ? styles.webSafeareaContainer
              : styles.safeareaContainer
          }>
          <ScrollView testID={tID('ExportDataDateRangeModal-ScrollView-1')}>
            <View style={styles.bottomContainer}>
              <Text
                testID={tID('ExportDataDateRangeModal-Text-Title')}
                color={colors.textPrimary}>
                Share Readings History
              </Text>
              <Text
                testID={tID('ExportDataDateRangeModal-Text-Subtitle')}
                variation="Body1Regular"
                color={colors.textMuted}
                topMargin={24}>
                Please select a date range you'd like to share. If you would
                like all your data please check the box down below
              </Text>
              <View style={styles.mt24}>
                <CalendarView
                  disabled={allSelected}
                  onSelectDate={onSelectDateRange}
                />
              </View>
              <Divider top={16} />
              <Checkbox
                selected={allSelected}
                onPress={() => setAllSelected(!allSelected)}
                topMargin={16}
                title="All Data"
                titleStyle={
                  allSelected
                    ? styles.selectedChecBoxTitle
                    : styles.normalChecBoxTitle
                }
                checkboxStyle={Platform.OS === 'web' ? styles.checkbox : {}}
              />
              <Divider top={16} />
              <View style={styles.buttonsContainer}>
                <Button
                  testID={tID('ExportDataDateRangeModal-Button-Cancel')}
                  style={[styles.button]}
                  buttonType={BUTTON_TYPES.Bordered}
                  title={'Cancel'}
                  onPress={() => onClose(false)}
                />
                <Button
                  testID={tID('ExportDataDateRangeModal-Button-Confirm')}
                  style={[styles.button, styles.ml16]}
                  buttonType={BUTTON_TYPES.Filled}
                  titleColor={colors.textOnAccent}
                  title={'Confirm'}
                  onPress={onPressConfirm}
                />
              </View>
            </View>
          </ScrollView>
        </SafeAreaView>
        {showLoader && (
          <LoadingModal
            testID={tID('ExportDataDateRangeModal-LoadingModal-Export')}
            visible={showLoader}
          />
        )}
      </View>
    </Modal>
    {/* Terminal popups are siblings of the export Modal (never nested) — see
        note above. Each is itself a Popup(Modal), and the export Modal is
        hidden while any of them is visible. */}
    <SuccessModal
      showSuccseeModal={showSuccseeModal}
      userEmail={userEmail}
      onPressResend={onPressResend}
      onCloseSuccessModal={onCloseSuccessModal}
    />
    {message != NERWORK_ERROR && (
      <ErrorModal
        testID={tID('ExportDataDateRangeModal-ErrorModal-Export')}
        visible={showErrorModal}
        message={message}
        onClose={onPressClose}
      />
    )}
    {message === NERWORK_ERROR && (
      <OfflineNotice
        testID={tID('ExportDataDateRangeModal-OfflineNotice-Network')}
        visible={showErrorModal}
        onClose={onPressClose}
      />
    )}
    </>
  );
};

export default ExportDataDateRangeModal;
